<template>

  <div>

    <user-list-add-new
      :is-add-new-user-sidebar-active.sync="isAddNewUserSidebarActive"
      @refetch-data="refetchData"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewUserSidebarActive = true"
              >
                <span class="text-nowrap">Add User</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refUserListTable"
        class="position-relative"
        :items="fetchUsers"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        style="min-height: 16rem;"
      >

        <!-- Column: User -->
        <template #cell(user)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.photo"
                :text="avatarText(data.item.full_name)"
                :variant="`light-${resolveUserRoleVariant(data.item.role)}`"
                :to="{ name: 'apps-users-view', params: { id: data.item.id } }"
              />
            </template>
            <b-link
              :to="{ name: 'apps-users-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.full_name }}
              <span
                v-if="data.item.job_description"
              >({{ data.item.job_description }})</span>
            </b-link>
            <b-badge
              v-if="!data.item.is_active"
              variant="danger"
            >
              Inactive
            </b-badge>

          </b-media>
        </template>

        <!-- Column: Role -->
        <template #cell(role)="data">
          <div class="text-nowrap">
            <feather-icon
              :icon="resolveUserRoleIcon(data.item.role)"
              size="18"
              class="mr-50"
              :class="`text-${resolveUserRoleVariant(data.item.role)}`"
            />
            <span class="align-text-top text-capitalize">{{ resolveUserRoleText(data.item.role) }}</span>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item :to="{ name: 'apps-users-view', params: { id: data.item.id } }">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">Details</span>
            </b-dropdown-item>
            <b-dropdown-item @click="sendInvite(data.item.email)">
              <feather-icon icon="SendIcon" />
              <span class="align-middle ml-50">Invite</span>
            </b-dropdown-item>
            <b-dropdown-item @click="sendReset(data.item.email)">
              <feather-icon icon="KeyIcon" />
              <span class="align-middle ml-50">Reset Password</span>
            </b-dropdown-item>
            <template
              v-if="isManager"
            >
              <b-dropdown-item
                v-if="data.item.role === 'manufacturer' || data.item.role ==='brand' || data.item.role ==='agent'"
                @click="enableManager(data.item.email)"
              >
                <feather-icon icon="UserPlusIcon" />
                <span class="align-middle ml-50">Enable Manager permission</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.role === 'manufacturer-manager' || data.item.role ==='brand-manager' || data.item.role ==='agent-manager'"
                @click="disableManager(data.item.email)"
              >
                <feather-icon icon="UserMinusIcon" />
                <span class="align-middle ml-50">Disable Manager permission</span>
              </b-dropdown-item>
            </template>
            <template
              v-if="isManager"
            >
              <b-dropdown-item
                v-if="data.item.is_active"
                @click="disableUser(data.item.email)"
              >
                <feather-icon icon="UserXIcon" />
                <span class="align-middle ml-50">Disable User</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!data.item.is_active"
                @click="enableUser(data.item.email)"
              >
                <feather-icon icon="UserCheckIcon" />
                <span class="align-middle ml-50">Enable User</span>
              </b-dropdown-item>
            </template>
          </b-dropdown>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalUsers"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>

import axios from '@axios'
import {
  BCard, BRow, BCol, BFormInput, BBadge, BButton, BTable, BMedia, BAvatar, BLink,
  BDropdown, BDropdownItem, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { ref, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { avatarText } from '@core/utils/filter'
import useUsersList from './useUsersList'
import userStoreModule from '../userStoreModule'
import UserListAddNew from './UserListAddNew.vue'

export default {
  components: {
    UserListAddNew,

    BBadge,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
  },
  data() {
    const userRole = JSON.parse(localStorage.getItem('userData')).role
    return {
      userRole,
      isManager: userRole === 'brand-manager' || userRole === 'manufacturer-manager' || userRole === 'agent-manager' || userRole === 'platform-manager',
    }
  },
  methods: {
    sendInvite(email) {
      axios.post('/api/v1/users/reset_password/', { email, invite: true })
        .then(() => this.$toast({
          component: ToastificationContent,
          props: {
            title: `Invite sent to ${email}`,
            icon: 'SentIcon',
            variant: 'success',
          },
        }))
    },
    sendReset(email) {
      axios.post('/api/v1/users/reset_password/', { email })
        .then(() => this.$toast({
          component: ToastificationContent,
          props: {
            title: `Reset sent to ${email}`,
            icon: 'SentIcon',
            variant: 'success',
          },
        }))
    },
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = 'app-user'

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const toast = useToast()
    const isAddNewUserSidebarActive = ref(false)

    const {
      fetchUsers,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserRoleText,

    } = useUsersList()

    const enableManager = email => {
      axios.post('/api/v1/users/enable_manager/', { email })
        .then(() => toast({
          component: ToastificationContent,
          props: {
            title: `Add manager permission to ${email}`,
            icon: 'UserPlusIcon',
            variant: 'success',
          },
        })).then(refUserListTable.value.refresh)
    }

    const disableManager = email => {
      axios.post('/api/v1/users/disable_manager/', { email })
        .then(() => toast({
          component: ToastificationContent,
          props: {
            title: `Disable manager permission to ${email}`,
            icon: 'UserMinusIcon',
            variant: 'success',
          },
        })).then(refUserListTable.value.refresh)
    }

    const enableUser = email => {
      axios.post('/api/v1/users/enable_user/', { email })
        .then(() => toast({
          component: ToastificationContent,
          props: {
            title: `User ${email} enabled`,
            icon: 'UserCheckIcon',
            variant: 'success',
          },
        })).then(refUserListTable.value.refresh)
    }

    const disableUser = email => {
      axios.post('/api/v1/users/disable_user/', { email })
        .then(() => toast({
          component: ToastificationContent,
          props: {
            title: `User ${email} disabled`,
            icon: 'UserXIcon',
            variant: 'success',
          },
        })).then(refUserListTable.value.refresh)
    }

    return {
      // Sidebar
      isAddNewUserSidebarActive,

      fetchUsers,
      tableColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      enableManager,
      disableManager,
      enableUser,
      disableUser,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserRoleText,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
